import { TaskVideoEditor } from "./TaskVideoEditor";
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import type { TaskStepDTO, TaskStepLinkDTO } from '../types/tasks.types';
import { uploadImageToCos, resolveImagePreviewUrl, resolveAdminImagePreviewUrl } from '../../../lib/upload/cos';
import { RichTaskTextEditor } from './RichTaskTextEditor';

interface TaskStepEditorProps {
  step: TaskStepDTO;
  index: number;
  total: number;
  inputClassName: string;
  onUpdate: (patch: Partial<TaskStepDTO>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
  allowVideo?: boolean;
}

export function TaskStepEditor({
  step,
  index,
  total,
  inputClassName,
  onUpdate,
  onMove,
  onRemove,
  onUploadingChange,
  disabled = false,
  allowVideo = true,
}: TaskStepEditorProps) {
  const [videoUploading, setVideoUploading] = useState(false);
  const uploadCallback = useRef(onUploadingChange); uploadCallback.current = onUploadingChange;
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedImages, setUploadedImages] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  useEffect(() => { uploadCallback.current?.(uploading || videoUploading); }, [uploading, videoUploading]);
  useEffect(() => () => uploadCallback.current?.(false), []);
  const images = step.images ?? [];
  const links = step.links ?? [];

  const handleImageFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    if (files.length === 0) return;

    try {
      setUploading(true);

      const results = await Promise.all(files.map((file) => uploadImageToCos(file, 'task-step')));
      setUploadedImages((prev) => {
        const next = { ...prev };
        for (const item of results) {
          next[item.relativePath] = item.readUrl;
        }
        return next;
      });
      onUpdate({ images: [...images, ...results.map((item) => item.relativePath)] });
    } catch {
      window.alert('图片上传失败，请重新选择。');
    } finally {
      setUploading(false);
    }
  };

  const appendImageUrl = () => {
    const url = imageUrl.trim();
    if (!url) return;
    onUpdate({ images: [...images, url] });
    setImageUrl('');
  };

  const removeImage = (imageIndex: number) => {
    onUpdate({ images: images.filter((_, i) => i !== imageIndex) });
  };

  const addLink = () => {
    onUpdate({ links: [...links, { label: '点击前往', url: '' }] });
  };

  const updateLink = (linkIndex: number, patch: Partial<TaskStepLinkDTO>) => {
    onUpdate({
      links: links.map((link, i) => (i === linkIndex ? { ...link, ...patch } : link)),
    });
  };

  const removeLink = (linkIndex: number) => {
    onUpdate({ links: links.filter((_, i) => i !== linkIndex) });
  };

  return (
    <div className="border border-gray-200 rounded-md p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500">步骤 {index + 1}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30">上移</button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30">下移</button>
          <button onClick={onRemove} className="text-xs text-red-400 hover:text-red-600">删除</button>
        </div>
      </div>

      <input type="text" value={step.title} onChange={(e) => onUpdate({ title: e.target.value })} placeholder="步骤标题" className={inputClassName} />
      <RichTaskTextEditor disabled={disabled} value={step.description} onChange={(value) => onUpdate({ description: value })} minRows={4} placeholder="步骤描述" />

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">示例图片</label>
        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {images.map((uri, imageIndex) => (
              <div key={`${uri.slice(0, 32)}-${imageIndex}`} className="relative overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                <img
                  src={resolveImagePreviewUrl(uri, uploadedImages) || resolveAdminImagePreviewUrl(uri) || undefined}
                  alt={`步骤 ${index + 1} 示例 ${imageIndex + 1}`}
                  className="h-24 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(imageIndex)}
                  className="absolute right-1 top-1 rounded bg-white/90 px-1.5 py-0.5 text-xs text-red-500 shadow-sm hover:bg-white"
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageFiles}
            disabled={disabled || uploading || videoUploading}
            className="w-full max-w-full text-sm text-gray-600"
          />
          <div className="flex w-full min-w-0 sm:flex-1 sm:min-w-[260px] items-center gap-2">
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="或粘贴图片 URL"
              className={inputClassName}
            />
            <button type="button" onClick={appendImageUrl} className="h-9 shrink-0 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50">添加</button>
          </div>
        </div>
        {uploading && <p className="text-xs text-gray-400">图片上传中...</p>}
      </div>

      {allowVideo && <TaskVideoEditor value={step.videos} onChange={videos=>onUpdate({videos})} disabled={disabled || uploading} onBusy={setVideoUploading} />}
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">步骤链接</label>
        <div className="space-y-2">
          {links.map((link, linkIndex) => (
            <div key={linkIndex} className="grid gap-2 md:grid-cols-[160px_1fr_auto]">
              <input
                type="text"
                value={link.label}
                onChange={(e) => updateLink(linkIndex, { label: e.target.value })}
                placeholder="前端按钮文案"
                className={inputClassName}
              />
              <input
                type="text"
                value={link.url}
                onChange={(e) => updateLink(linkIndex, { url: e.target.value })}
                placeholder="https://example.com"
                className={inputClassName}
              />
              <button
                type="button"
                onClick={() => removeLink(linkIndex)}
                className="h-9 rounded-md border border-red-200 px-3 text-sm text-red-500 hover:bg-red-50"
              >
                删除
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addLink}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
          >
            + 添加链接
          </button>
        </div>
        <p className="text-xs text-gray-400">按钮文案会直接显示在客户端；未写协议时客户端默认按 https 处理。</p>
      </div>
    </div>
  );
}
